// Profile-invariant inventory and per-number cap management (docs/03 §9.1a).
// Carrier purchase/release is gated by number_provisioning_enabled.
import { useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { requestErrorDescription } from "../lib/errors";
import {
  isSharedNumber,
  listPhoneNumbers,
  purchasePhoneNumber,
  releasePhoneNumber,
  searchAvailableNumbers,
  useNumberProvisioning,
  type PhoneNumberSearchResult,
} from "../lib/phoneNumbers";
import { queryKeys } from "../lib/queryKeys";
import {
  Badge,
  Button,
  EmptyState,
  Inline,
  InlineError,
  Page,
  PageHeader,
  Section,
  Skeleton,
  Stack,
  Text,
  TextInput,
  useToast,
} from "../ui";
import { PhoneNumberCapacity } from "./PhoneNumberCapacity";
import { SharedNumbers } from "./SharedNumbers";

function offeredLabel(item: PhoneNumberSearchResult): string {
  return [item.locality, item.region]
    .filter((part): part is string => part !== null && part !== "")
    .join(", ");
}

export function PhoneNumbersSettings() {
  const provisioning = useNumberProvisioning();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [areaCode, setAreaCode] = useState("");
  const [offered, setOffered] = useState<PhoneNumberSearchResult[]>([]);

  const numbers = useQuery({
    queryKey: queryKeys.phoneNumbers.list(),
    queryFn: listPhoneNumbers,
  });

  const search = useMutation({
    mutationFn: () => searchAvailableNumbers(areaCode),
    onSuccess: (results) => {
      setOffered(results);
      if (results.length === 0) {
        toast.error("No numbers available", {
          description: "The carrier has no local numbers for that area code.",
        });
      }
    },
    onError: (error: unknown) => {
      toast.error("Search failed", {
        description: requestErrorDescription(error),
      });
    },
  });

  const purchase = useMutation({
    mutationFn: (address: string) => purchasePhoneNumber(address),
    onSuccess: async (purchased) => {
      setOffered((current) =>
        current.filter((item) => item.address !== purchased.address),
      );
      await queryClient.invalidateQueries({
        queryKey: queryKeys.phoneNumbers.all,
      });
      toast.success(`Bought ${purchased.address}`);
    },
    onError: (error: unknown) => {
      toast.error("Purchase failed", {
        description: requestErrorDescription(error),
      });
    },
  });

  const release = useMutation({
    mutationFn: (id: string) => releasePhoneNumber(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: queryKeys.phoneNumbers.all,
      });
      toast.success("Number released");
    },
    onError: (error: unknown) => {
      toast.error("Release failed", {
        description: requestErrorDescription(error),
      });
    },
  });

  // One list, two ownerships: only org-owned rows are releasable, so the two
  // groups render as separate sections rather than one list with a Release
  // button that silently does not appear on half the rows. `listPhoneNumbers`
  // walks every keyset page, so this split sees the COMPLETE inventory — a
  // partial page would drop the shared section, whose rows sort last.
  const owned = (numbers.data ?? []).filter(
    (number) => !isSharedNumber(number),
  );
  const shared = (numbers.data ?? []).filter(isSharedNumber);

  if (provisioning.isPending) {
    return <Skeleton shape="detail-field" />;
  }

  return (
    <Page>
      <PageHeader
        description="Numbers this organization holds with Marker's carrier."
        title="Phone numbers"
      />
      <Stack>
        {provisioning.enabled && (
          <Section heading="Buy a number">
            <Stack>
              <Text>
                US local numbers only. Numbers outside the US need an approved
                carrier regulatory bundle, which Marker does not support yet.
              </Text>
              <Inline
                align="end"
                className="marker-field-row marker-field-row--compact"
                gap={3}
              >
                <TextInput
                  inputMode="numeric"
                  label="Area code"
                  maxLength={3}
                  onChange={(event) => {
                    setAreaCode(event.target.value);
                  }}
                  placeholder="415"
                  value={areaCode}
                />
                <Button
                  disabled={search.isPending}
                  onClick={() => {
                    search.mutate();
                  }}
                >
                  Search
                </Button>
              </Inline>
              {offered.map((item) => (
                <Inline key={item.address}>
                  <Text>{item.address}</Text>
                  <Text>{offeredLabel(item)}</Text>
                  <Button
                    disabled={purchase.isPending}
                    onClick={() => {
                      purchase.mutate(item.address);
                    }}
                    variant="secondary"
                  >
                    Buy
                  </Button>
                </Inline>
              ))}
            </Stack>
          </Section>
        )}

        <Section
          description={
            provisioning.enabled
              ? "Releasing gives a number back to the carrier and stops its monthly charge. A number on a live call, or pinned by a persona, cannot be released until that changes."
              : "Set per-number caps to match your private SIP trunk capacity."
          }
          heading="Numbers you hold"
        >
          {numbers.isPending && <Skeleton shape="detail-field" />}
          {numbers.isError && (
            <InlineError>{requestErrorDescription(numbers.error)}</InlineError>
          )}
          {owned.length === 0 && !numbers.isPending && !numbers.isError && (
            <EmptyState
              description={
                provisioning.enabled
                  ? "Buy one above to give personas a number of your own to call from."
                  : "Ask your operator to sync your private SIP trunk numbers."
              }
              title="No numbers yet"
            />
          )}
          <Stack>
            {owned.map((number) => (
              <Inline key={number.id}>
                <Text>{number.address}</Text>
                <Badge>{number.status}</Badge>
                <PhoneNumberCapacity
                  key={`${number.id}:${String(number.max_concurrent_calls)}`}
                  number={number}
                />
                {provisioning.enabled && (
                  <Button
                    disabled={release.isPending}
                    onClick={() => {
                      release.mutate(number.id);
                    }}
                    variant="secondary"
                  >
                    Release
                  </Button>
                )}
              </Inline>
            ))}
          </Stack>
        </Section>

        <SharedNumbers numbers={shared} />
      </Stack>
    </Page>
  );
}
